import { Button } from "@mui/material";
import { useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { createLogger } from "../../../helper/logger";
import Header from "../../../shared/layouts/header";
import { DiscardChangesDialog, ValidationErrorDialog } from "./Dialogs";
import OutputReviewGrid from "./OutputReviewGrid";
import { compareAndFilter, validateGridData } from "./helpers";
import { useOutputReviewData } from "./useOutputReviewData";

/**
 * Output Review component for reviewing and confirming output analysis
 */
const OutputReview = () => {
	const logger = createLogger("OutputReview");
	logger.debug("Component rendering");

	const gridRef = useRef(null);
	const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false);
	const [isValidationDialogOpen, setIsValidationDialogOpen] = useState(false);
	const [validationErrMsg, setValidationErrMsg] = useState(null);

	const { moduleId, caseID, requestID } = useParams();
	logger.debug("Route params", { moduleId, caseID, requestID });
	const navigate = useNavigate();
	// Use our custom hook for data management
	logger.debug("About to call useOutputReviewData with caseID", { caseID });
	const {
		loading,
		rowData,
		originalRowData,
		totalRows,
		datasource,
		onRowDataUpdated,
		postData,
	} = useOutputReviewData(caseID);

	logger.debug("Data from useOutputReviewData", {
		loading,
		rowDataLength: rowData?.length || 0,
		originalRowDataLength: originalRowData?.length || 0,
		totalRows,
		hasDatasource: !!datasource,
	});
	// Handle saving draft
	const onSaveAsDraft = async () => {
		logger.debug("Save as draft triggered");
		logger.debug("Current row data lengths", {
			rowData: rowData?.length,
			originalRowData: originalRowData?.length,
		});

		const validation = validateGridData(gridRef, rowData, originalRowData);
		logger.debug("Validation result", validation);

		if (validation.isValid) {
			if (validation.editedRows.length > 0) {
				logger.debug("Sending edited rows to postData", {
					rows: validation.editedRows,
				});
				const result = await postData(validation.editedRows, "save");
				logger.debug("postData result", result);

				if (result.data !== "Results saved successfully.") {
					logger.error("Error saving results", { error: result.error });
					toast.error(result.error || "Error saving results");
					return;
				}
			} else {
				logger.debug("No edited rows to save");
			}
			toast.success(`The Case ID: ${caseID} has been successfully saved`);
			navigate(`/${moduleId}/wb-home`);
		} else if (validation.formNumberError) {
			logger.warn("Form number validation error", {
				error: validation.formNumberError,
			});
			setValidationErrMsg(validation.formNumberError);
			setIsValidationDialogOpen(true);
		} else {
			logger.warn("General validation error", {
				error: validation.errorMessage,
			});
			setValidationErrMsg(validation.errorMessage);
			setIsValidationDialogOpen(true);
		}
	};

	// Handle confirming results
	const onConfirm = async () => {
		logger.debug("Confirm results triggered");
		logger.debug("Current row data lengths", {
			rowData: rowData?.length,
			originalRowData: originalRowData?.length,
		});

		const validation = validateGridData(gridRef, rowData, originalRowData);
		logger.debug("Validation result", validation);

		if (validation.isValid) {
			logger.debug("Sending edited rows to postData with 'confirm' action", {
				rows: validation.editedRows,
			});
			const result = await postData(validation.editedRows, "confirm");
			logger.debug("Confirm result", result);

			if (
				result.data !== "Results confirmed successfully." &&
				result.data !== "Results saved and confirmed successfully." &&
				result.data !==
					"Results confirmed successfully but update Status to the OneFRP failed."
			) {
				logger.error("Error confirming results", { error: result.error });
				toast.error(result.error || "Error confirming results");
				return;
			}

			toast.success(
				`The result reports for the Case ID: ${caseID} has been successfully generate`,
			);
			navigate(`/${moduleId}/wb-home`);
		} else if (validation.formNumberError) {
			logger.warn("Form number validation error", {
				error: validation.formNumberError,
			});
			setValidationErrMsg(validation.formNumberError);
			setIsValidationDialogOpen(true);
		} else {
			logger.warn("General validation error", {
				error: validation.errorMessage,
			});
			setValidationErrMsg(validation.errorMessage);
			setIsValidationDialogOpen(true);
		}
	};

	// Handle closing with potential unsaved changes
	const onClose = () => {
		const alterFormNumberData = compareAndFilter(
			rowData,
			originalRowData,
			"formNumber",
			null,
			null,
		);

		const alterPermanentStatusData = compareAndFilter(
			rowData,
			originalRowData,
			"permanentStatus",
			null,
			null,
		);

		const alterConfirmResultData = compareAndFilter(
			rowData,
			originalRowData,
			"confirmResult",
			null,
			null,
		);

		if (
			alterFormNumberData.length > 0 ||
			alterPermanentStatusData.length > 0 ||
			alterConfirmResultData.length > 0
		) {
			setIsDiscardDialogOpen(true);
		} else {
			navigate(`/${moduleId}/wb-home`);
		}
	};

	// Dialog handlers
	const handleCancelDiscard = () => setIsDiscardDialogOpen(false);

	const handleDiscardChanges = () => {
		setIsDiscardDialogOpen(false);
		toast.success(`The changes made to Case ID: ${caseID} has been discarded`);
		navigate(`/${moduleId}/wb-home`);
	};

	const handleCloseValidation = () => {
		setValidationErrMsg(null);
		setIsValidationDialogOpen(false);
	}; // Format numbers for display

	logger.debug("Before render", {
		rowDataItems: rowData?.length || 0,
		loadingState: loading,
	});

	return (
		<>
			<div
				id="content-wrapper"
				className="d-flex flex-column"
				style={{ flex: "1" }}
			>
				<div id="content">
					<Header caseID={`Request ID: ${requestID}`} />
					<div className="container-fluid">
						<hr className="sidebar-divider my-0" />
						<div className="row">
							<div className="col-xl-12 col-md-6 mb-4" />
						</div>
						<div className="row">
							<div className="col-xl-12 col-md-12 mb-4">
								<div className="card shadow mb-4">
									<div className="card-header py-3 d-flex flex-row align-items-center justify-content-between" >
										<h6 className="infoMessage">
										This output was generated by an AI system and may require further review
										</h6>
									</div>
									<div className="card-body">
										<OutputReviewGrid
											gridRef={gridRef}
											rowData={rowData}
											datasource={datasource}
											getRowId={(params) => params.data.sn.toString()}
											onRowDataUpdated={onRowDataUpdated}
										/>
									</div>
									<div>
										<div className="col-xl-12 col-md-12 mb-12">
											<div className="review-button">
												<Button
													className={
														rowData.length === 0
															? "btnprimary-disable"
															: "btnprimary"
													}
													onClick={onSaveAsDraft}
													disabled={rowData.length === 0}
												>
													Save
												</Button>
												<Button
													className={
														rowData.length === 0
															? "btnprimary-disable"
															: "btnprimary"
													}
													onClick={onConfirm}
													disabled={rowData.length === 0}
												>
													Confirm
												</Button>
												<Button className="btnprimary" onClick={onClose}>
													Close
												</Button>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				{/* Dialogs */}
				<DiscardChangesDialog
					open={isDiscardDialogOpen}
					onCancel={handleCancelDiscard}
					onDiscard={handleDiscardChanges}
				/>

				<ValidationErrorDialog
					open={isValidationDialogOpen}
					onClose={handleCloseValidation}
					errorMessage={validationErrMsg}
				/>
			</div>
		</>
	);
};

export default OutputReview;
